import { Lightbulb } from 'lucide-react'
import React, { useState, useCallback } from 'react'
import type { Area } from 'react-easy-crop'
import Cropper from 'react-easy-crop'

import { useUploadAndSaveUserImage } from 'src/shared/api/media/use-upload-and-save-image'
import ProfilePhoto from 'src/shared/assets/images/profile-avatar-fallback.png'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Input } from 'src/shared/components/ui/inputs/input'
import { ResponsivePopup } from 'src/shared/components/ui/popups/responsive-popup'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useUserPhoto } from 'src/shared/features/users/api/accounts/use-user-photo'
import { validateImageFile } from 'src/shared/features/users/components/profile-photos/cover-photo'
import { useToast } from 'src/shared/hooks/use-toast'
import { getCroppedImg } from 'src/shared/utils/crop-image'

interface ProfilePhotoUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  trigger?: React.ReactNode
}

const ProfilePhotoUploadDialog: React.FC<ProfilePhotoUploadDialogProps> = ({ open, onOpenChange, trigger }) => {
  const { data: user } = useMyUserData()
  let { data: userPhoto } = useUserPhoto('profile_photo', user!.id)
  const { toast } = useToast()

  if (!userPhoto || userPhoto == '') {
    userPhoto = ProfilePhoto
  }

  const uploadAndSaveImage = useUploadAndSaveUserImage()

  const initialCrop = {
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  }

  const [imagePreview, setImagePreview] = useState<string>(userPhoto)
  const [editingImage, setEditingImage] = useState<boolean>(false)
  const [crop, setCrop] = useState({ x: initialCrop.x, y: initialCrop.y })
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>(initialCrop)

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate the file
    const validationError = await validateImageFile(file)
    if (validationError) {
      toast({
        title: 'Invalid image',
        description: validationError,
        variant: 'destructive',
      })
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      if (reader.result) {
        setEditingImage(true)
        setCroppedAreaPixels(initialCrop)
        setImagePreview(reader.result as string)
      }
    }
    reader.readAsDataURL(file)
  }

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const handleSubmit = async () => {
    if (!croppedAreaPixels || !imagePreview) return

    try {
      const croppedImage = await getCroppedImg(imagePreview, croppedAreaPixels, 1)
      if (!croppedImage) throw new Error('Failed to crop image')
      await uploadAndSaveImage.mutateAsync({
        uri: croppedImage,
        photoType: 'profile_photo',
      })
      setImagePreview(croppedImage)
      setEditingImage(false)
      onOpenChange(false)
    } catch (error) {
      console.error('Error saving image:', error)
    }
  }

  const handleCancel = () => {
    setImagePreview(userPhoto || ProfilePhoto)
    setEditingImage(false)
    setCroppedAreaPixels(initialCrop)
    setCrop({ x: initialCrop.x, y: initialCrop.y })
  }

  const content = (
    <div className="flex flex-col items-center justify-center space-y-20">
      <div className="space-y-2 ">
        <div className="relative size-[300px] overflow-hidden rounded-lg">
          {editingImage ? (
            <Cropper
              image={imagePreview}
              initialCroppedAreaPixels={croppedAreaPixels}
              crop={crop}
              zoomSpeed={2}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onInteractionStart={() => setEditingImage(true)}
              aspect={1}
              objectFit="contain"
              cropShape="round"
              showGrid={false}
              style={{
                containerStyle: { position: 'relative', backgroundColor: '#1B3029', width: '100%', height: '100%' },
                mediaStyle: { width: '100%', height: '100%', objectFit: 'contain' },
              }}
            />
          ) : (
            <img src={imagePreview} alt="profile photo" className="size-full object-cover" />
          )}
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Lightbulb className="size-4" />
            <span className="text-sm text-muted">Ensure high resolution picture.</span>
          </div>
          <div className="flex items-center gap-2">
            <Lightbulb className="size-4" />
            <span className="text-sm text-muted">Make sure to have a good lighting</span>
          </div>
        </div>
      </div>
      <div className="flex justify-between gap-3">
        {!editingImage ? (
          <>
            {/* <Button variant="outline" onClick={() => alert('TODO: Delete photo')} className="w-full">
              Delete
            </Button> */}
            <Button variant="default" onClick={() => document.getElementById('fileInput')?.click()} className="w-full">
              Change Photo
            </Button>
          </>
        ) : (
          <>
            <Button variant="link" onClick={handleCancel} className="w-full">
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSubmit}
              className="w-full"
              isLoading={uploadAndSaveImage.isPending}
            >
              {uploadAndSaveImage.isPending ? 'Uploading...' : 'Submit'}
            </Button>
          </>
        )}
      </div>
      <Input id="fileInput" type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
    </div>
  )

  return (
    <ResponsivePopup open={open} onOpenChange={onOpenChange} trigger={trigger} title="Upload Profile Photo">
      {content}
    </ResponsivePopup>
  )
}

export default ProfilePhotoUploadDialog
